import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { api, apiError, TECH_FUNCTIONS, OUVREUSE_FUNCTIONS, COMPLEMENT_FUNCTIONS, CATERING_FUNCTIONS, ALL_FUNCTIONS, HOUR_BRACKETS } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Save, Copy } from "lucide-react";

function EuroInput({ value, onChange, testId }) {
  return (
    <div className="relative">
      <span className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">€</span>
      <Input
        type="number"
        value={value ?? ""}
        onChange={onChange}
        className="h-8 text-xs text-right pl-5"
        data-testid={testId}
      />
    </div>
  );
}

function SalaryTable({ title, functions, ville, grid, onChange, singleHourly = false, hourlyLabel = "Coût horaire (€ brut)", testId }) {
  return (
    <div className="space-y-2">
      <h2 className="font-display text-lg font-bold">{title}</h2>
      <div className="rounded-xl border border-border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm border-collapse" data-testid={testId}>
            <thead>
              <tr className="bg-secondary text-muted-foreground">
                <th className="px-3 py-2 text-left font-semibold min-w-[280px] border-r border-border/50">Fonction</th>
                {singleHourly ? (
                  <th className="px-3 py-2 text-right font-semibold w-40">{hourlyLabel}</th>
                ) : (
                  HOUR_BRACKETS.map((h) => (
                    <th key={h} className="px-3 py-2 text-right font-semibold w-28 border-r border-border/50 last:border-r-0">{h}h (€ brut)</th>
                  ))
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {functions.map((fonction) => {
                const cell = grid[`${ville}|||${fonction}`] || {};
                return (
                  <tr key={fonction} className="hover:bg-secondary/30">
                    <td className="px-3 py-2 font-medium border-r border-border/50">{fonction}</td>
                    {singleHourly ? (
                      <td className="px-2 py-1">
                        <EuroInput
                          value={cell.taux_horaire}
                          onChange={(e) => onChange(fonction, "taux_horaire", e.target.value)}
                          testId={`salary-${fonction}-taux`}
                        />
                      </td>
                    ) : (
                      HOUR_BRACKETS.map((h) => (
                        <td key={h} className="px-2 py-1 border-r border-border/50 last:border-r-0">
                          <EuroInput
                            value={cell[`h${h}`]}
                            onChange={(e) => onChange(fonction, `h${h}`, e.target.value)}
                            testId={`salary-${fonction}-${h}`}
                          />
                        </td>
                      ))
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default function SalairesPage() {
  const [villes, setVilles] = useState([]);
  const [ville, setVille] = useState("");
  const [target, setTarget] = useState("");
  const [grid, setGrid] = useState({}); // key `${ville}|||${fonction}` -> {h4,...,taux_horaire}
  const [saving, setSaving] = useState(false);
  const [duplicating, setDuplicating] = useState(false);
  const [scope, setScope] = useState("all");

  const load = useCallback(async () => {
    try {
      const [{ data: sug }, { data: salaries }] = await Promise.all([
        api.get("/suggestions"),
        api.get("/salaries"),
      ]);
      setVilles(sug.villes || []);
      if (!ville && sug.villes?.length) setVille(sug.villes[0]);
      const g = {};
      (salaries || []).forEach((s) => {
        g[`${s.ville}|||${s.fonction}`] = {
          h4: s.h4 || "", h6: s.h6 || "", h8: s.h8 || "", h10: s.h10 || "", h12: s.h12 || "",
          taux_horaire: s.taux_horaire || "",
        };
      });
      setGrid(g);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    }
  }, [ville]);

  useEffect(() => { load(); }, []); // eslint-disable-line

  const setCell = (fonction, field, val) => {
    const key = `${ville}|||${fonction}`;
    setGrid((g) => ({ ...g, [key]: { ...(g[key] || {}), [field]: val } }));
  };

  const buildRows = (targetVille, functions) => functions.map((fonction) => {
    const c = grid[`${ville}|||${fonction}`] || {};
    return {
      ville: targetVille, fonction,
      h4: c.h4 || "", h6: c.h6 || "", h8: c.h8 || "", h10: c.h10 || "", h12: c.h12 || "",
      taux_horaire: c.taux_horaire || "",
    };
  });

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/salaries", { salaries: buildRows(ville, ALL_FUNCTIONS) });
      toast.success(`Salaires enregistrés pour ${ville}`);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const duplicate = async () => {
    const funcs = scope === "techs" ? TECH_FUNCTIONS : scope === "ouvreuses" ? OUVREUSE_FUNCTIONS : scope === "complements" ? COMPLEMENT_FUNCTIONS : scope === "catering" ? CATERING_FUNCTIONS : ALL_FUNCTIONS;
    const targets = target === "__ALL__" ? villes.filter((v) => v !== ville) : (target && target !== ville ? [target] : []);
    if (!ville || !targets.length) return;
    const scopeLabel = scope === "techs" ? "les techs" : scope === "ouvreuses" ? "les ouvreuses" : scope === "complements" ? "les compléments" : scope === "catering" ? "les aides catering" : "toute la feuille";
    const targetLabel = target === "__ALL__" ? `toutes les villes (${targets.length})` : target;
    if (!window.confirm(`Copier ${scopeLabel} de ${ville} vers ${targetLabel} ? Les montants existants seront remplacés.`)) return;
    setDuplicating(true);
    try {
      let rows = [];
      targets.forEach((t) => { rows = rows.concat(buildRows(t, funcs)); });
      await api.put("/salaries", { salaries: rows });
      setGrid((g) => {
        const ng = { ...g };
        targets.forEach((t) => funcs.forEach((f) => { ng[`${t}|||${f}`] = { ...(g[`${ville}|||${f}`] || {}) }; }));
        return ng;
      });
      toast.success(`${scopeLabel[0].toUpperCase()}${scopeLabel.slice(1)} copié(e)s vers ${targets.length} ville(s)`);
      setTarget("");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setDuplicating(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight">Salaires</h1>
          <p className="text-sm text-muted-foreground mt-1">Montant du cachet brut (€) par fonction, pour chaque ville</p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <Select value={ville} onValueChange={setVille}>
            <SelectTrigger className="w-[190px]" data-testid="salaries-ville-selector"><SelectValue placeholder="Ville" /></SelectTrigger>
            <SelectContent>
              {villes.map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button onClick={save} disabled={saving || !ville} className="bg-primary text-primary-foreground hover:bg-primary/90 font-semibold" data-testid="salaries-save-button">
            <Save className="h-4 w-4 mr-2" /> {saving ? "Enregistrement..." : "Enregistrer"}
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3 rounded-xl border border-border bg-card p-3">
        <span className="text-sm text-muted-foreground">Dupliquer</span>
        <Select value={scope} onValueChange={setScope}>
          <SelectTrigger className="w-[180px]" data-testid="salaries-duplicate-scope"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Toute la feuille</SelectItem>
            <SelectItem value="techs">Techs uniquement</SelectItem>
            <SelectItem value="ouvreuses">Ouvreuses uniquement</SelectItem>
            <SelectItem value="complements">Compléments uniquement</SelectItem>
            <SelectItem value="catering">Aides Catering uniquement</SelectItem>
          </SelectContent>
        </Select>
        <span className="text-sm text-muted-foreground">de <strong className="text-foreground">{ville || "…"}</strong> vers</span>
        <Select value={target} onValueChange={setTarget}>
          <SelectTrigger className="w-[190px]" data-testid="salaries-duplicate-target"><SelectValue placeholder="Ville cible" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__ALL__">Toutes les villes</SelectItem>
            {villes.filter((v) => v !== ville).map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}
          </SelectContent>
        </Select>
        <Button variant="outline" onClick={duplicate} disabled={duplicating || !ville || !target} data-testid="salaries-duplicate-button">
          <Copy className="h-4 w-4 mr-2" /> {duplicating ? "Copie..." : "Dupliquer"}
        </Button>
      </div>

      <SalaryTable title="Intermittents (techs)" functions={TECH_FUNCTIONS} ville={ville} grid={grid} onChange={setCell} testId="salaries-table-techs" />
      <SalaryTable title="Compléments" functions={COMPLEMENT_FUNCTIONS} ville={ville} grid={grid} onChange={setCell} singleHourly hourlyLabel="Montant (€ brut)" testId="salaries-table-complements" />
      <SalaryTable title="Aides Catering" functions={CATERING_FUNCTIONS} ville={ville} grid={grid} onChange={setCell} singleHourly testId="salaries-table-catering" />
      <SalaryTable title="Ouvreuses" functions={OUVREUSE_FUNCTIONS} ville={ville} grid={grid} onChange={setCell} singleHourly testId="salaries-table-ouvreuses" />
    </div>
  );
}
